import DatabaseModel from './DatabaseModel.js';
import FileUtils from '../FileUtils.js';
import { EventBus } from '../EventBus.js';

/**
 * Adaptador del módulo FACT-COBRO (#167): la pestaña FACTURACIÓN del Cuadro de mandos y el
 * Centro de Cobros (#187, #188, #189).
 *
 * Lleva la lectura de la Tarjeta B · comparativa por ejercicios (#171), las cuatro lecturas del
 * Centro de Cobros · La cartera (#187, #201), las dos de la bandeja de asignación (#188), las cinco
 * de «Mi trabajo de hoy» (#190), las del informe de Abonos abiertos y sus programaciones (#193) y las
 * tres operaciones de la Tarjeta A · control diario (#170): leer un mes, guardar uno de sus cuatro
 * datos manuales y recalcular uno de sus dos objetivos (#249).
 *
 * **Casi todo es lectura**, y conviene que las escrituras sigan siendo fáciles de ver desde aquí:
 * `asignarGestor` (#188); `crearGestionCobro` y `anotarPromesa` (#190); el envío y las
 * programaciones del informe de abonos (#193); y `guardarDatoManual` y `recalcularObjetivo`, que
 * son de la Tarjeta A.
 *
 * URLs verificadas contra FactCobroComparativaController.java, FactCobroCarteraController.java,
 * FactCobroBandejaController.java, FactCobroTrabajoHoyController.java y FactCobroMesController.java
 * (los cinco con @RequestMapping("/factcobro")).
 *
 * Los dos permisos NO son el mismo y conviene no confundirlos:
 *
 *  - La **pestaña FACTURACIÓN** es de ADMIN y DIRECTOR (D-ROL): a quien no tenga el rol la entrada
 *    no se le enseña, y si llegara aquí el servidor contesta 403.
 *  - El **Centro de Cobros** no niega la entrada: recorta (D-COB-5). Cada comercial ve su cartera,
 *    cada gestor la suya, y Administración, Directores y Administrador lo ven todo. La respuesta
 *    dice en `alcance` qué parte se está viendo.
 *
 * El adaptador no decide ninguno de los dos, solo no los esconde.
 */
const FactCobro = Object.create(DatabaseModel);

FactCobro.model = 'factcobro';

/**
 * El código con el que la API avisa de que **la pestaña está cerrada** (#169): la tabla de los meses
 * no garantiza una sola ficha por mes, así que el servidor no sirve cifras de las que no puede
 * responder. Viaja en un 503 con el texto para la persona en `error.message`.
 *
 * Está aquí, en el adaptador, porque es parte del contrato con la API —lo escribe
 * `FactCobroCandadoMes.CODIGO_PESTANA_CERRADA`— y lo van a mirar las dos tarjetas: la B (#171) y la
 * A (#170). Con la cadena copiada en cada pantalla, la segunda lo escribiría distinto y enseñaría un
 * error genérico justo cuando hay algo concreto que decir.
 */
FactCobro.CODIGO_PESTANA_CERRADA = 'FACTCOBRO_PESTANA_CERRADA';

/**
 * El código con el que la API avisa de que **no se sabe si un correo salió** (#547): el mensaje llegó
 * a entregarse al servidor de correo y la conexión se cortó después, así que Office365 pudo haberlo
 * aceptado. Viaja en un 500 con el texto para la persona en `error.message`.
 *
 * Por qué es un código y no una frase que la pantalla reconozca leyéndola: lo que cuelga de esto es
 * si se deja repetir el envío, y eso no puede depender de que dos textos sigan escritos igual. Lo
 * escribe `FactCobroControlDiarioReporteService.CODIGO_ENVIO_INCIERTO` y lo mira
 * `comoAcaboElEnvio` en la pantalla del control diario.
 *
 * **No es lo mismo que un 500 cualquiera.** Un envío que no salió —la lista vacía, el correo
 * rechazado, el mensaje que no se pudo componer— se puede repetir sin más; esto no, porque el
 * informe puede estar ya en el buzón de alguien.
 */
FactCobro.CODIGO_ENVIO_INCIERTO = 'FACTCOBRO_ENVIO_INCIERTO';

/**
 * La comparativa por ejercicios, con las dos magnitudes en la misma respuesta. Sin parámetros: la
 * ventana de comparación la decide el servidor a partir de los meses cerrados que hay guardados.
 *
 * `cached=false`: son cifras de dirección y la pantalla enseña un sello de cuándo son (#16); servir
 * una foto de hace un minuto bajo un sello que la anuncia como fresca es lo que ese sello viene a
 * evitar.
 */
FactCobro.comparativa = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/comparativa',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * El Centro de Cobros · La cartera (#187): todo lo que la pantalla pinta, de una sola lectura.
 *
 * Es un POST llevando los filtros en el cuerpo y **sigue siendo una lectura**: no escribe nada. Van
 * por el cuerpo y no por la URL porque son listas —hasta seis a la vez, y la de clientes puede
 * traer decenas de códigos—, y eso en una cadena de consulta no se lee, no se depura y puede
 * pasarse del tope de longitud de un proxy. Mismo criterio que el `/search/` de los listados.
 *
 * `cached=false`: son cifras de dirección y la pantalla enseña un sello de cuándo son (#16), igual
 * que la comparativa.
 *
 * Dentro de la respuesta viajan además dos paneles que **no tienen lectura propia a propósito**:
 *
 *  - `escalera`, la **escalera de la reclamación** (#200).
 *  - `personas`, **las personas** (#189): la tabla por gestor de cobro, la tabla por empleado de
 *    ventas y la fila de aviso de la cartera sin asignar.
 *  - `calidad`, **la calidad del dato** (#201): las dos alertas de incoherencia, la tabla de lo que
 *    falta por cumplimentar y el listado de las gestiones que D-COB-12 deja sin atribuir. La lista
 *    que abre cada una de sus filas sí tiene lectura propia: `documentosDeLaCalidad`, y
 *    `gestionesSinAtribuirDeLaCalidad` para los vínculos que el tope de la tabla recorta.
 *
 * Los tres salen de esta misma pasada, así que sus sumas son el total de documentos abiertos sin
 * depender de que dos consultas vean la misma cartera. Y a los tres **no los recortan los filtros**
 * que van en el cuerpo —hablan de la cartera visible entera, igual que `dosCifras`—, así que no hay
 * que volver a pedirlos al cambiar un filtro.
 *
 * @param {Object} filtro los filtros de pantalla; `{}` o nada devuelve la cartera completa visible.
 */
FactCobro.cartera = function(filtro) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/cartera',
        method: 'POST',
        contentType: 'application/json',
        data: filtro || {}
    }, false);
};

/**
 * Solo la cifra de la tarjeta OED · Cobros del Cuadro de mandos: el pendiente total y cuántos
 * documentos lo forman.
 *
 * Existe para que la rejilla de tarjetas no tenga que pedir la pantalla entera —tabla,
 * agrupaciones y desplegables— para escribir un número. El número es el **mismo** que el de la
 * tarjeta «Pendiente total» de la pantalla: lo calcula el servidor de la misma lectura (D-COB-11,
 * «se reutiliza, no se duplica»).
 */
FactCobro.resumenCartera = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/cartera/resumen',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Las tareas de gestión de cobro de una factura: el último nivel de la navegación (criterio 9).
 *
 * La lista viaja en `list`, no en `dto`. Y el servidor responde 403 tanto si la factura no existe
 * como si no está en la cartera de quien pregunta: son el mismo caso a propósito.
 *
 * @param {String} idFactura el id de la factura.
 */
FactCobro.gestionesDeFactura = function(idFactura) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/cartera/' + idFactura + '/gestiones',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Las siete filas del bloque «La calidad del dato» (COB-5c, #201), con el nombre que espera la API
 * (`FactCobroCalidadCampoEnum`). Están aquí, en el adaptador, por lo mismo que `DATOS_MANUALES`:
 * son parte del contrato, y es el valor con el que se construye la URL de la lista de cada fila.
 *
 * Lo normal es que no haya que escribirlos a mano: cada fila de la respuesta trae su propio
 * `campo`, que es el que la pantalla manda. Esto es el respaldo y la documentación de qué valores
 * admite la API.
 */
FactCobro.CAMPOS_CALIDAD = {
    PGC: 'PGC',
    HPGC: 'HPGC',
    FECHA_PREVISTA_COBRO: 'FECHA_PREVISTA_COBRO',
    FECHA_ULTIMO_COBRO: 'FECHA_ULTIMO_COBRO',
    PLAZO_DIAS: 'PLAZO_DIAS',
    COBRADO_TOTAL_ABIERTA: 'COBRADO_TOTAL_ABIERTA',
    EMPRESA_EXTINTA: 'EMPRESA_EXTINTA'
};

/**
 * La lista que abre una fila del bloque de calidad del dato (#201, criterio 3): los documentos
 * abiertos a los que afecta ese campo, con cliente, número, vencimiento y saldo pendiente.
 *
 * Tiene lectura propia y no viaja dentro de `cartera` **a propósito**: el criterio pide que la
 * lista sea completa —la fila del PGC dice hoy 928 documentos— y cinco listas completas en cada
 * carga serían la cartera entera metida cinco veces en la misma respuesta. El recuento y el
 * porcentaje sí vienen ahí, que es lo que se lee sin pinchar.
 *
 * **No lleva los filtros de pantalla**, y no es un olvido: el bloque se calcula sobre la cartera
 * visible entera, así que la lista tiene que hablar del mismo conjunto que la fila. Si le llegaran
 * los filtros, la lista traería menos documentos de los que dice la fila y el criterio 3 no se
 * podría comprobar.
 *
 * La lista viaja en `list`, no en `dto`, igual que las gestiones de una factura. Y el servidor
 * responde 403 si ese campo no es ninguna de las siete filas: una lista vacía se leería como «en
 * ese campo no falta nada».
 *
 * `cached=false` por lo mismo que `cartera`: es la lista de lo que hay que ir a arreglar, y servir
 * una foto de hace un minuto ofrecería arreglar documentos que ya están arreglados.
 *
 * @param {String} campo uno de `CAMPOS_CALIDAD`.
 */
FactCobro.documentosDeLaCalidad = function(campo) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/cartera/calidad/' + campo
            + '/documentos',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * El listado ENTERO de las gestiones de cobro que D-COB-12 deja sin atribuir (#201): un apunte por
 * vínculo, con las señas que traía y por qué no señala a ninguna factura.
 *
 * La tabla del bloque trae esa lista con tope, porque viaja en cada carga de la pantalla, y su pie
 * dice cuántos vínculos no han cabido. Esto es lo que ese pie abre: un aviso de que faltan
 * registros sin forma de verlos deja inaccesible justo lo que el bloque viene a identificar.
 *
 * **No es una fila del bloque** y por eso no se pide con un `CAMPOS_CALIDAD`: estos vínculos son
 * tareas T0045 que no han podido señalar a ningún documento de la cartera, así que no hay campo que
 * pasarle. Tiene ruta propia.
 *
 * La lista viaja en `list`, igual que las otras dos. `cached=false` por lo mismo que `cartera`.
 */
FactCobro.gestionesSinAtribuirDeLaCalidad = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model
            + '/cartera/calidad/gestiones-sin-atribuir',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * La vista «La gestión» del Centro de Cobros (#199): facturas sin ninguna gestión, silencios y
 * actividad por gestor y semana, sacados de las tareas de gestión de cobro (hoy T0045).
 *
 * **Lectura propia y no dentro de `cartera`**, como el cuadro de comportamiento: necesita las
 * tareas de las últimas semanas por fecha, que es otra consulta, y cambiar un filtro de la cartera
 * no tiene por qué repetirla. Igual que la escalera, habla de la cartera visible entera.
 *
 * Un hueco llega como `null` y se pinta como raya, nunca como cero (D-COB-8). «Cobrado tras
 * gestión» llega siempre nulo hasta la #197, con su motivo escrito en `motivoCobradoTrasGestion`.
 *
 * @param {Number} semanas cuántas semanas enseña la tabla de actividad; sin él, doce.
 */
FactCobro.gestionCartera = function(semanas) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/cartera/gestion'
            + (semanas ? '?semanas=' + encodeURIComponent(semanas) : ''),
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Cambia el N de los silencios de «La gestión» (#199): **la única escritura de esa vista**.
 *
 * El servidor contesta 403 a quien no ve toda la cartera y a un número fuera de 1..365, y lo que
 * devuelve es el valor **releído** después de guardar, no el que se pidió.
 *
 * Es **condicional**: va con la versión de lo guardado que se leyó, y si ya no es la vigente el
 * servidor no escribe y dice qué consta. Es lo que impide que un guardado que se quedó sin
 * respuesta y llega tarde pise a otro posterior.
 *
 * @param {Number} dias con cuántos días sin gestión entra una factura vencida en la lista.
 * @param {String} version la `silencios.versionDias` de la última lectura, tal cual.
 */
FactCobro.cambiarDiasDeSilencio = function(dias, version) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/cartera/gestion/silencio-dias',
        method: 'PUT',
        contentType: 'application/json',
        data: { dias: dias, version: version }
    }, false);
};

/**
 * El cuadro «Comportamiento por forma de pago» del Centro de Cobros (#202): cómo paga de verdad
 * cada condición de pago, para decidir la del próximo pedido con el historial delante.
 *
 * **Lectura propia y no dentro de `cartera`**, al contrario que `escalera` y `personas`. Esos dos
 * salen de la misma pasada que la cartera; este no: necesita las facturas del periodo **de
 * cualquier estado**, que es otro conjunto y otra consulta, y además tiene su propio periodo.
 * Metido en `cartera`, cada cambio de un filtro arrastraría la lectura del periodo entero sin que
 * nadie la hubiera pedido.
 *
 * Es un POST llevando el periodo en el cuerpo y **sigue siendo una lectura**: no escribe nada, no
 * cambia el plazo pactado de ninguna factura y no decide ninguna condición de pago.
 *
 * `cached=false` por lo mismo que `cartera`: son cifras de dirección y la pantalla enseña un sello
 * de cuándo son (#16).
 *
 * Dos cosas del contrato que conviene saber antes de pintar nada:
 *
 *  - **El pendiente NO lo recorta el periodo.** Es la cartera abierta de hoy (D-COB-1), que es la
 *    cifra que tiene que cuadrar con el listado de facturas pendientes del día. El periodo solo
 *    acota lo facturado y lo cobrado.
 *  - **Un hueco llega como `null` y se pinta como raya, nunca como cero** (D-COB-8): una forma de
 *    pago sin facturas medibles no tiene un retraso medio de cero días, no tiene retraso medio.
 *
 * @param {Object} filtro `{anioDesde, mesDesde, anioHasta, mesHasta}`; `{}` o nada mira los
 *                 últimos doce meses contando el mes en curso.
 */
FactCobro.comportamientoPago = function(filtro) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/comportamiento-pago',
        method: 'POST',
        contentType: 'application/json',
        data: filtro || {}
    }, false);
};

/**
 * La bandeja de asignación del gestor de cobro (#188): las facturas que hay que repartir.
 *
 * Es un POST llevando el filtro en el cuerpo —mismo criterio que `cartera`— y **sigue siendo una
 * lectura**: lo que escribe es `asignarGestor`, y solo eso.
 *
 * `cached=false`: la bandeja cambia en cuanto alguien asigna, y servir una foto de hace un minuto
 * en la pantalla desde la que se asigna es ofrecer marcar facturas que ya tienen dueño.
 *
 * @param {Object} filtro `{modo, gestores}`. `{}` o nada devuelve la bandeja de las sin gestor.
 */
FactCobro.bandeja = function(filtro) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/bandeja',
        method: 'POST',
        contentType: 'application/json',
        data: filtro || {}
    }, false);
};

/**
 * Asigna el gestor de cobro a las facturas marcadas: **la única escritura de todo el Centro de
 * Cobros**.
 *
 * El servidor contesta 403 a quien no puede asignar (criterio 5), y lo demás NO es un error: una
 * factura que se ha cobrado entre medias o que ya tenía ese gestor viene en `omitidas` con su
 * motivo, y las que sí se podían se asignan igual. O sea que una respuesta correcta puede traer
 * `asignadas: 0`, y la pantalla tiene que leer el resultado, no dar por hecho que ha ido bien.
 *
 * El recuento de `asignadas` que devuelve ya está **releído** por el servidor: solo cuenta la
 * factura que ha quedado con su gestor y con su entrada en el histórico (criterio 3).
 *
 * @param {Array} ids los `_id` de las facturas marcadas.
 * @param {String} gestor el `username` del empleado al que se asignan.
 */
FactCobro.asignarGestor = function(ids, gestor) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/bandeja/asignar',
        method: 'POST',
        contentType: 'application/json',
        data: { ids: ids || [], gestor: gestor }
    }, false);
};

/**
 * Si quien pregunta entra en «Mi trabajo de hoy» (#190) y con qué alcance.
 *
 * **ESTO NO ES UN GATE, ES LA PREGUNTA**: contesta `200` con `puedeEntrar: false` y el motivo, para
 * que la barrera de la ruta y el enlace del Centro de Cobros puedan **no dibujar** la puerta. Un
 * botón que lleva a un 403 es peor que no tener botón: hace creer que te han quitado permisos
 * (regla de pantalla de dirección, 05/10/2026). La puerta la cierran los otros cuatro endpoints.
 *
 * Y por qué hay que preguntárselo al servidor en vez de mirar los roles aquí: entran Administración,
 * Dirección y Administrador, y entra **cada gestor de cobro**; «gestor de cobro» no es un rol del
 * sistema, es quien figura como PGC en alguna factura (D-COB-3), un dato que el navegador no conoce.
 * Un comercial no entra, y un comercial al que mañana le asignen cartera sí.
 *
 * `cached=false`: decide qué se enseña y de qué se deja entrar; una foto de hace un minuto deja
 * fuera a quien acaban de asignarle cartera.
 */
FactCobro.accesoTrabajoHoy = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/trabajo-hoy/acceso',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * «Mi trabajo de hoy» (#190): la cabecera con la bandeja de #188, las diez facturas vencidas de más
 * prioridad y las notas que explican el orden.
 *
 * Es un POST llevando el filtro en el cuerpo —mismo criterio que `cartera` y `bandeja`— y **sigue
 * siendo una lectura**: lo que escribe son `crearGestionCobro` y `anotarPromesa`.
 *
 * `cached=false`: la lista cambia en cuanto alguien crea una gestión, y servir una foto de hace un
 * minuto en la pantalla desde la que se gestiona es ofrecer llamar a quien ya ha llamado otro.
 *
 * **Lo que la respuesta decide y esta pantalla no**: de quién es la lista (`gestorAplicado`, que
 * viaja *aplicado* y no *pedido*), si se puede elegir gestor (`puedeElegirGestor`), si se puede
 * repartir la bandeja (`puedeAsignar`) y el motivo de cada ficha (`motivo`). Nada de eso se deduce
 * aquí: el servidor es quien aplica el recorte de D-COB-5 y quien calcula el orden.
 *
 * @param {Object} filtro `{gestor, sinGestor}`. `{}` o nada devuelve la lista que le toca a quien
 *                 pregunta. A quien no ve toda la cartera el servidor le ignora el filtro y le
 *                 contesta la suya.
 */
FactCobro.trabajoHoy = function(filtro) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/trabajo-hoy',
        method: 'POST',
        contentType: 'application/json',
        data: filtro || {}
    }, false);
};

/**
 * El historial de una factura de «Mi trabajo de hoy»: sus gestiones de cobro y sus promesas, lo
 * último primero, más la tarea que el formulario de promesa propone (`idTareaSugerida`).
 *
 * Las dos cosas en una respuesta porque se leen juntas: lo que el gestor necesita antes de llamar es
 * la secuencia completa —«la llamé el 12, prometió pagar el 20, no pagó, la volví a llamar el 25»—.
 *
 * **`gestiones` son todas y `idsGestionesElegibles` las que se pueden elegir**, y no son lo mismo:
 * una promesa es lo que el cliente dijo *durante* esa gestión, así que una T0045 planificada para
 * mañana se enseña en el historial pero no se puede anotar sobre ella. Quién es elegible lo decide el
 * servidor —el mismo sitio que lo decide al guardar— y cuando no hay ninguna manda escrito el por qué
 * en `porqueNoSePuedeAnotar`. El formulario solo puede ofrecer las de esa lista: ofrecer una que la
 * escritura va a rechazar es el defecto, no un detalle (decisión técnica del 15/10/2026).
 *
 * El servidor responde 403 tanto si la factura no existe como si no está en su lista de trabajo: son
 * el mismo caso a propósito.
 *
 * @param {String} idFactura el id de la factura.
 */
FactCobro.historialTrabajoHoy = function(idFactura) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/trabajo-hoy/'
            + idFactura + '/historial',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Crea la tarea **T0045 · GESTIÓN DE COBRO** de esa factura, ya rellena: tipo, empleado, fecha y
 * hora de inicio, y vinculada a la factura y al cliente (#190, criterio 1).
 *
 * **Es una ESCRITURA, y es idempotente por su `clave`**: la pantalla genera una referencia antes de
 * mandarla, y el reintento de la misma pulsación —el que hace falta cuando la respuesta se pierde por
 * el camino— trae la misma y recibe la gestión que ya se creó, sin crear otra. Lo que no se deduplica
 * es dos llamadas de verdad al mismo cliente el mismo día: ésas traen claves distintas y son dos
 * gestiones. La clave es **obligatoria**: sin ella no se puede saber después si la escritura ocurrió,
 * que es justo lo que hay que poder preguntar (ver `FactCobro.operacionTrabajoHoy`).
 *
 * La tarea la crea el servicio de tareas que ya existía, así que nace con su código de la serie del
 * usuario y aparece en la pestaña Tareas de la factura y en la consulta de Tareas como cualquier
 * otra. **El modelo y la consulta de Tareas no cambian.**
 *
 * La respuesta viene **releída** de la base de datos y puede traer `avisos`: lo que no haya quedado
 * como tenía que quedar —el vínculo con el cliente, la hora de inicio— se dice en vez de afirmarlo.
 * O sea que una respuesta correcta puede traer avisos, y la pantalla tiene que leerlos.
 *
 * @param {String} idFactura el id de la factura.
 * @param {String} clave la referencia de esta pulsación, de `FactCobro.claveDeOperacion()`.
 */
FactCobro.crearGestionCobro = function(idFactura, clave) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/trabajo-hoy/'
            + idFactura + '/gestion?clave=' + encodeURIComponent(clave || ''),
        method: 'POST',
        contentType: 'application/json',
        data: {}
    }, false);
};

/**
 * Qué ha pasado con una escritura de «Mi trabajo de hoy»: la de **esa clave** (#190).
 *
 * Es la lectura con la que la pantalla resuelve una respuesta que se perdió por el camino, y existe
 * porque el desenlace **no se puede deducir** de si la ficha ha cambiado: con la foto igual, la
 * pantalla se quedaba bloqueada para siempre si la petición no llegó a salir; con la foto distinta,
 * daba por guardada la propia cuando la que había cambiado la foto era la de otra sesión (revisión
 * CODEX, rondas 7 y 8).
 *
 * Contesta **cuatro** estados y ninguno por omisión, y trae escrita la frase que se enseña
 * (`explicacion`), para que no haya dos redacciones del mismo desenlace:
 *
 *  - `HECHA` — se escribió; `idResultado` dice qué salió. No hay que repetirla.
 *  - `EN_CURSO` — está dentro del servidor. No se repite: se vuelve a preguntar.
 *  - `NO_ESTA` — se ha ido a mirar y de esa clave no hay nada escrito, así que no se escribió.
 *    Volver a mandarla es seguro: si la primera llegara con retraso, su clave ya estaría reclamada
 *    y no habría dos.
 *  - `NO_SE_SABE` — **no se ha podido comprobar**, y eso no es ninguno de los otros tres. Entró con
 *    la decisión de dirección del 10/10/2026: el servidor ya no deduce el desenlace, lo busca, y
 *    cuando no puede buscarlo lo dice en vez de contestar «en curso» o «no está». Tratarlo como lo
 *    segundo autorizaría un reintento que puede duplicar.
 *
 * Y trae `revisaElHistorial` cuando lo único que queda por hacer es mirar las gestiones de esa
 * factura: cuando no se ha podido comprobar, y cuando la búsqueda ha encontrado **más de una**
 * gestión que podía ser ésa. Viaja como un dato y no solo dentro de la frase para que la pantalla
 * pueda poner el control de verdad al lado del aviso.
 *
 * @param {String} clave la referencia que se mandó con la escritura.
 */
FactCobro.operacionTrabajoHoy = function(clave) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/trabajo-hoy/operacion/'
            + encodeURIComponent(clave || ''),
        method: 'GET',
        data: {}
    }, false);
};

/** Cuántas referencias ha pedido esta pestaña: solo se usa en el respaldo sin `crypto`. */
let clavesPedidas = 0;

/**
 * Una referencia nueva para una escritura de «Mi trabajo de hoy».
 *
 * Vive aquí, en el adaptador, porque **es parte del contrato**: el servidor la valida con una forma
 * concreta (letras, cifras, guiones y puntos, de 8 a 80) y la usa como `_id` del acuse de la
 * operación. Con el generador escrito en la pantalla, un cambio de forma se vería como un rechazo al
 * guardar y no antes.
 *
 * Sin `crypto.randomUUID`: el navegador mínimo que soporta este front no lo tiene. El azar sale de
 * `crypto.getRandomValues`, que sí tienen todos los navegadores de la ERP, y **no de
 * `Math.random`**: de esta cadena depende que una escritura no se duplique —el servidor la usa como
 * `_id` del acuse— y un generador pseudoaleatorio no promete reparto ni independencia entre
 * pestañas (Sonar lo marca como punto de seguridad, `javascript:S2245`, con razón). Mismo
 * planteamiento que `normas-vacaciones.vue`, que generaba la identidad de un intento de
 * publicación.
 *
 * El instante va delante para que dos escrituras del mismo milisegundo no dependan solo del azar, y
 * donde no hubiera `crypto` queda un contador de la propia pestaña, que dentro de ella no se repite
 * nunca; dos pestañas a la vez sin `crypto` es el único hueco que queda, y no se da en los
 * navegadores de la ERP.
 *
 * Que la clave no se repita **importa más de lo que parece**: hoy el servidor trata cualquier clave
 * ya reclamada como «esta misma escritura otra vez» sin comparar de qué operación era (revisión
 * CODEX, ronda 9), así que dos personas con la misma clave se contestarían con la escritura de la
 * otra. Esa comparación está pendiente de una decisión de diseño y no se arregla desde aquí; lo que
 * sí se puede hacer aquí es que la coincidencia no dependa de un generador que no la promete.
 */
FactCobro.claveDeOperacion = function() {
    const cripto = typeof window !== 'undefined' && window.crypto;
    let azar;
    if (cripto && cripto.getRandomValues) {
        const bytes = new Uint8Array(12);
        cripto.getRandomValues(bytes);
        azar = Array.prototype.map
            .call(bytes, function(b) { return ('0' + b.toString(16)).slice(-2); })
            .join('');
    } else {
        azar = 'c' + (++clavesPedidas);
    }
    return 'c' + Date.now().toString(36) + '-' + azar;
};

/**
 * Anota la **promesa de pago** que el cliente ha dado en una gestión (#190, criterio 9).
 *
 * Es la otra **escritura** de la pantalla y se guarda en una colección propia del módulo
 * (`factcobro_promesa_pago`), atada a la factura y a la T0045 que la originó: **no se añade ningún
 * campo a las tareas ni a las facturas** (D-COB-3).
 *
 * Los tres campos son obligatorios y el servidor **no rellena ninguno** (D-COB-8): rechaza con un
 * mensaje concreto —y sin escribir nada— la petición a la que le falte uno. Que el formulario
 * *proponga* el saldo pendiente como importe y «Pendiente» como resultado no es lo mismo: eso lo ve
 * la persona escrito delante y lo puede cambiar antes de guardar.
 *
 * **El `comentario` es el cuarto campo y el único opcional** (dirección, 19/10/2026): lo que dijo el
 * cliente, en texto libre. No va en la tarea: va con la promesa, y el servidor lo *copia* además al
 * campo Comentarios que la tarea ya tiene para que se lea desde Tareas y desde el calendario. Si esa
 * copia no se puede hacer, la respuesta lo dice en `avisos` y la promesa está guardada igual.
 *
 * @param {String} idFactura el id de la factura.
 * **Es idempotente por `claveOperacion`**, que es obligatoria y la genera la pantalla con
 * `FactCobro.claveDeOperacion()` antes de mandar: volver a mandar la misma anotación —lo que hace
 * falta cuando su respuesta se pierde— no guarda una segunda promesa, devuelve la que se guardó.
 *
 * @param {Object} promesa `{idTarea, importeComprometido, fechaComprometida, resultado, comentario,
 *                 claveOperacion}`. `fechaComprometida` en `YYYY-MM-DD`; `resultado`, uno de
 *                 `FactCobro.RESULTADOS_PROMESA`; `comentario` opcional.
 */
FactCobro.anotarPromesa = function(idFactura, promesa) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/trabajo-hoy/'
            + idFactura + '/promesa',
        method: 'POST',
        contentType: 'application/json',
        data: promesa || {}
    }, false);
};

/**
 * En qué puede quedar una promesa de pago, con el nombre que espera la API
 * (`FactCobroPromesaResultadoEnum`). Están aquí, en el adaptador, por lo mismo que
 * `DATOS_MANUALES`: son parte del contrato, y la pantalla construye con ellos el desplegable del
 * formulario, así que con la lista escrita en el `.vue` un nombre mal puesto se vería como un
 * rechazo al guardar y no antes.
 *
 * El rótulo de cada uno lo manda la API en la propia promesa (`rotuloResultado`), para que la
 * pantalla no tenga que mantener su propia tabla de nombres; esto es la lista de valores válidos.
 */
/**
 * Cuánto cabe en el **comentario** de una promesa de pago, el mismo número que
 * `FactCobroGestionCobroEscritura.TOPE_COMENTARIO` en la API.
 *
 * Está aquí y no en la pantalla por lo mismo que `RESULTADOS_PROMESA`: es parte del contrato. El que
 * decide es el servidor —rechaza con su mensaje lo que no cabe, y no lo recorta, porque un
 * comentario cortado por la mitad pone en boca del gestor media frase—; el formulario lo usa de
 * `maxlength` para que no se llegue a chocar con él.
 */
FactCobro.TOPE_COMENTARIO_PROMESA = 4000;

FactCobro.RESULTADOS_PROMESA = [
    { valor: 'PENDIENTE', rotulo: 'Pendiente · todavía no se sabe' },
    { valor: 'CUMPLIDA', rotulo: 'Cumplida · pagó lo prometido' },
    { valor: 'PARCIAL', rotulo: 'Cumplida a medias · pagó una parte' },
    { valor: 'INCUMPLIDA', rotulo: 'Incumplida · no pagó' }
];

/**
 * El informe **Abonos abiertos** (COB-7, #193): los abonos con Estado = Abierta, su antigüedad, el
 * bloque de los que llevan más de un año sin casar, el reparto por año y la sugerencia de con qué
 * factura casaría cada uno.
 *
 * Es un GET y **no escribe nada**: no casa abonos, no los modifica y no toca ninguna otra pantalla
 * (D-COB-9). La sugerencia es una sugerencia.
 *
 * **Aquí el permiso SÍ cierra**, al contrario que en la cartera: el servidor contesta **403** a
 * quien no sea Administración, Director ni Administrador (D-COB-5, criterio 5). Un informe vacío se
 * leería como «no hay abonos abiertos», que es decir algo falso de la cartera de la empresa.
 *
 * `cached=false`: es la lista de trabajo desde la que Administración va cerrando abonos, así que una
 * foto de hace un minuto ofrecería casar lo que ya está casado.
 */
FactCobro.abonosAbiertos = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/abonos',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Manda el informe de ahora mismo por correo: el botón **Enviar reporte**.
 *
 * Devuelve el número de destinatarios que ha tenido el envío, para que la pantalla pueda decir
 * cuántos en vez de un «enviado» que nadie puede comprobar.
 *
 * @param {Object} datos `{emails, emailsCc, emailsCco, asunto}`. Sin `emails` el servidor contesta
 *        que no hay a quién mandarlo, y eso es un error que la pantalla tiene que enseñar: un envío
 *        a cero destinatarios que dijera «enviado» es lo peor de los dos mundos.
 */
FactCobro.enviarInformeAbonos = function(datos, operacion) {
    const self = this;
    return self.sendRequest({
        url: conOperacion(self.server_movilidad + self.prefix + self.model + '/abonos/enviar',
            operacion),
        method: 'POST',
        contentType: 'application/json',
        data: datos || {}
    }, false);
};

/**
 * La URL de una escritura de este informe con su **clave de operación** en el parámetro `operacion`.
 *
 * EN LA URL Y NO EN UNA CABECERA (#766). Hasta el 20/10/2026 viajaba en la cabecera `X-Operacion`, y
 * en producción ninguna de estas escrituras llegaba al servidor: allí la pantalla y la API están en
 * orígenes distintos, el navegador pregunta antes con un `OPTIONS`, y el filtro CORS de
 * `digitaltpm-core` que lo contesta solo admite `x-requested-with`, `authorization` y
 * `content-type`. El navegador cancelaba la petición sin mandarla y `sendRequest` la veía como un
 * `status === 0`, o sea como «se cerró sin respuesta». En local y en el preview la API va por el
 * mismo origen y por eso no se vio. Un parámetro de la URL no pasa por esa lista: es lo que ya hace
 * «Mi trabajo de hoy» con su `clave`. **No vuelvas a meter aquí una cabecera propia.**
 *
 * POR QUÉ EXISTE. Decisión técnica de @Donchurru del 15/10/2026, después de cuatro rondas de
 * revisión sobre la misma avería: dos escrituras a la vez sobre la misma programación. Lo que había
 * era un cerrojo en el navegador, y un cerrojo de navegador no cierra nada —lo salta un corte de
 * red, dos pestañas o dos personas—.
 *
 * QUÉ GARANTIZA. El servidor acepta la clave con un `insert` atómico: si vuelve la **misma clave con
 * el mismo contenido**, contesta el resultado que ya dio y no escribe nada. Por eso, cuando una
 * respuesta se pierde, la pantalla puede **repetir la misma operación** sin riesgo de duplicarla, en
 * vez de tener que elegir entre no saber nada y crear a ciegas.
 *
 * LO QUE TIENE QUE HACER QUIEN LLAMA: **conservar la clave entre reintentos**. Una clave nueva por
 * pulsación convierte el reintento en una operación distinta, que es justo lo que no se quiere. La
 * genera y la conserva la pantalla: `informe-abonos-cobros.vue` para este informe, y
 * `facturacion-control-diario.vue` para el alta de sus programaciones (#549).
 *
 * Y es **obligatoria**: sin ella el servidor rechaza la escritura. Una clave opcional sería una
 * garantía que no está, porque el camino sin clave volvería a ser el de antes.
 *
 * EL PARÁMETRO ES EL MISMO Y LA GARANTÍA TAMBIÉN, desde el 19/10/2026. Las dos pantallas entran al
 * **mismo registro de operaciones del servidor**, con su ventana de rescate y su consulta de estado,
 * y las dos guardan la clave en el navegador antes de lanzar la petición
 * (`operacionesAbonosCobros.js`). Lo que separa a los dos informes es el módulo, que el servidor
 * mete dentro de la clave: la misma cadena en `operacion` son dos operaciones distintas.
 *
 * Antes no era así —en el control diario la clave solo fijaba el `_id` del alta— y eso costó tres
 * rondas de revisión: una garantía escrita a mano tiene bordes propios que se tapan de uno en uno.
 *
 * Sin clave la URL sale tal cual, y el servidor contesta que falta: es el mismo rechazo que antes
 * daba una cabecera vacía, y una edición del control diario —que no la usa— no lleva nada de más.
 */
function conOperacion(url, operacion) {
    if (!operacion) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'operacion=' + encodeURIComponent(operacion);
}

/**
 * Las programaciones de envío del informe: el **Programar reporte** de siempre.
 *
 * POR QUÉ ESTAS CINCO LLAMADAS NO VAN A `/notificacion`, que es donde viven las de OEA y OEB. El
 * **motor es el mismo** —las reglas se guardan en la misma colección, las ejecuta el mismo
 * planificador y se registran en el mismo historial (D-COB-10: no se crea un motor nuevo)—, pero
 * `/notificacion` exige ADMIN, DIRECTOR o DTYCAL y **ADMINISTRACION no está**, que es justo para
 * quien es este informe. Con la pantalla llamando allí, la persona de Administración vería los
 * botones y recibiría un 403 al pulsarlos; y ampliar esa lista cambiaría quién programa los informes
 * de las otras pestañas, que el invariante del programa prohíbe tocar.
 *
 * La lista viaja en `list`, no en `dto`.
 */
FactCobro.programacionesAbonos = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/abonos/programaciones',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Da de alta o modifica una programación de envío.
 *
 * El **módulo lo pone el servidor**, así que no hace falta mandarlo y mandarlo no sirve de nada: es
 * lo que impide que por aquí se escriba una regla de otra pestaña.
 *
 * @param {Object} programacion `{id?, nombre, descripcion, frecuencia, hora, diaSemana, diaMes,
 *        soloLaborables, emailsExtra, activa}`.
 */
FactCobro.guardarProgramacionAbonos = function(programacion, operacion) {
    const self = this;
    return self.sendRequest({
        url: conOperacion(self.server_movilidad + self.prefix + self.model + '/abonos/programaciones',
            operacion),
        method: 'POST',
        contentType: 'application/json',
        data: programacion || {}
    }, false);
};

/**
 * Activa o pausa una programación.
 *
 * `activaActual` es **lo que la pantalla tenía delante**, y el servidor escribe el contrario sólo si
 * la regla sigue así. No es un dato redundante: con un «lee, niega y guarda» en el servidor, dos
 * pulsaciones casi a la vez leen las dos lo mismo y la segunda deshace la primera sin que nadie lo
 * diga («el último estado confirmado no puede reemplazarse con una foto antigua», @Donchurru
 * 15/10/2026). Si ya no coincide, no se cambia nada y se contesta que recargues.
 */
FactCobro.alternarProgramacionAbonos = function(id, activaActual, operacion) {
    const self = this;
    return self.sendRequest({
        url: conOperacion(self.server_movilidad + self.prefix + self.model + '/abonos/programaciones/' + id
            + '/activa?activaActual=' + (activaActual ? 'true' : 'false'), operacion),
        method: 'PUT',
        contentType: 'application/json',
        data: {}
    }, false);
};

/**
 * Borra una programación, y con eso **no queda ningún envío suelto** (criterio 7): el planificador
 * no lleva cola propia, lee las reglas activas en cada pasada. Lo que queda es el historial de lo
 * que ya se mandó, que no es un envío pendiente.
 */
FactCobro.borrarProgramacionAbonos = function(id, visto, operacion) {
    const self = this;
    const desde = visto === null || visto === undefined ? '' : '?visto=' + visto;
    return self.sendRequest({
        url: conOperacion(self.server_movilidad + self.prefix + self.model + '/abonos/programaciones/' + id
            + desde, operacion),
        method: 'DELETE',
        data: {}
    }, false);
};

/**
 * Manda ahora el informe de una programación, a sus destinatarios.
 *
 * Pasa por el mismo camino que el envío programado, así que lo que sale por el botón es exactamente
 * lo que saldrá a su hora. Devuelve el estado del envío (`ENVIADO`, `PARCIAL` o `ERROR`), y un
 * `ERROR` llega **con la petición en 200**: es el resultado del envío, no un fallo de la llamada, así
 * que la pantalla tiene que leerlo y no dar por bueno el `then`.
 */
FactCobro.enviarProgramacionAbonos = function(id, operacion) {
    const self = this;
    return self.sendRequest({
        url: conOperacion(self.server_movilidad + self.prefix + self.model + '/abonos/programaciones/' + id
            + '/enviar', operacion),
        method: 'POST',
        contentType: 'application/json',
        data: {}
    }, false);
};

/**
 * **Cómo acabó una operación de escritura de este informe.**
 *
 * POR QUÉ EXISTE. Opción **D** de la decisión técnica de @Donchurru del 15/10/2026. Hasta ahora,
 * cuando una respuesta se perdía, la pantalla **deducía** qué había pasado mirando otra lista: las
 * programaciones o el historial de envíos. Y hay dos sitios donde esas listas no dicen nada —el
 * envío con «Enviar reporte», que a propósito no deja apunte en «Últimos envíos», y la pestaña que
 * se cerró—, así que el defecto salía ronda tras ronda en un sitio distinto. El registro de
 * operaciones del servidor ya sabía cómo acabó cada una; esto es poder preguntárselo.
 *
 * QUÉ CONTESTA. Una de cuatro en `situacion`, con el texto ya redactado en `texto`:
 *
 *  - `NO_CONSTA` — no llegó, o se rechazó sin escribir nada. **No se ha aplicado nada.**
 *  - `EN_CURSO` — aceptada y sin empezar. Se resuelve esperando y volviendo a preguntar.
 *  - `DESCONOCIDO` — se puso en marcha y no consta cómo acabó: **puede estar aplicada**, y eso no se
 *    va a poder saber con más certeza. Es la del envío cuyo SMTP se cortó después de aceptar el
 *    correo.
 *  - `HECHA` — terminó; `resultado` y `reglaId` son lo que dio.
 *
 * ES DE SOLO LECTURA DE VERDAD: no ejecuta ni rescata el efecto, así que se puede preguntar en cada
 * recarga sin consecuencias. Y solo contesta por las operaciones de quien pregunta: la clave la
 * compone el servidor con el usuario autenticado.
 *
 * `cached=false` por lo mismo que el resto: una foto de hace un minuto diría «en curso» de algo que
 * ya terminó, y justo esa respuesta es la que decide si se desbloquea el formulario.
 */
FactCobro.estadoOperacionAbonos = function(operacion) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/abonos/operaciones/'
            + encodeURIComponent(operacion),
        method: 'GET',
        data: {}
    }, false);
};

/**
 * El **historial de envíos** de este informe: qué salió, cuándo, a cuántos buzones y con qué
 * resultado (`ENVIADO`, `PARCIAL` o `ERROR`, con el motivo cuando hay).
 *
 * Es el registro de siempre (`notificacion_logs`) filtrado por el módulo de este informe, así que
 * por aquí no se ven los envíos de OEA ni de OEB. Y va por esta puerta por lo mismo que las otras
 * cinco: `/notificacion/logs/{modulo}` exige ADMIN, DIRECTOR o DTYCAL, y sin esto Administración
 * podría programar el informe y no podría ver si llegó. Cuando un envío falla, este registro es lo
 * único que lo cuenta.
 *
 * La lista viaja en `list`, no en `dto`.
 */
FactCobro.enviosInformeAbonos = function(limit) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/abonos/programaciones/envios',
        method: 'GET',
        data: { limit: limit || 20 }
    }, false);
};

/**
 * Los cuatro datos que se teclean a mano, con el nombre que espera la API
 * (`FactCobroDatoManualEnum`). Están aquí, en el adaptador, porque son parte del contrato: la
 * pantalla construye con ellos las columnas editables y el cuerpo de la petición, así que con la
 * lista escrita en el `.vue` un nombre mal puesto se vería como un 400 al guardar y no antes.
 */
FactCobro.DATOS_MANUALES = {
    POEDD: 'POEDD',
    COBRO_REAL: 'COBRO_REAL',
    DEPOSITO: 'DEPOSITO',
    TOTAL_DEPOSITO: 'TOTAL_DEPOSITO'
};

/**
 * Los dos objetivos del mes, con el nombre que espera la API (`FactCobroMagnitudEnum`). Están aquí
 * por lo mismo que `DATOS_MANUALES`: son parte del contrato, y la pantalla los usa para construir
 * la URL del recálculo.
 *
 * Lo normal es que no haya que escribirlos a mano en ninguna parte: la respuesta del mes trae en
 * cada bloque de resumen su propia `magnitud`, que es la que la pantalla manda. Esto es el respaldo
 * y la documentación de qué valores admite la API.
 */
FactCobro.OBJETIVOS = {
    FACTURACION: 'FACTURACION',
    COBRO_REAL: 'COBRO_REAL'
};

/**
 * Un mes del control diario: los dos objetivos con su cumplimiento, el día a día con las doce
 * columnas ya calculadas, la fila de totales y el desglose del pie.
 *
 * `cached=false` por lo mismo que la comparativa, y además porque esta pantalla escribe: servir una
 * foto de hace un minuto después de guardar un cobro enseñaría el valor de antes y haría dudar de
 * si se guardó.
 */
FactCobro.mes = function(anio, mes) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/' + anio + '/' + mes,
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Guarda **uno** de los cuatro datos manuales de un día y devuelve el mes releído, con las columnas
 * derivadas y el pie ya recalculados.
 *
 * `valorVisto` es el importe que ese dato tenía **en la pantalla** cuando se empezó a editar, y no
 * es opcional: es el guardado condicional del servidor. Si ese dato ya no vale eso —otra persona lo
 * ha cambiado mientras esta pantalla estaba abierta— el servidor contesta 409 y no escribe nada, en
 * vez de pisar el cambio de la otra persona en silencio. `null` significa «en la pantalla estaba en
 * blanco», que es distinto de «no lo mando».
 */
FactCobro.guardarDatoManual = function(anio, mes, fecha, dato, valorVisto, valor) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/' + anio + '/' + mes
            + '/dato-manual',
        method: 'PUT',
        data: {
            fecha: fecha,
            dato: dato,
            valorVisto: valorVisto === undefined ? null : valorVisto,
            valor: valor === undefined ? null : valor
        }
    }, false);
};

/**
 * Recalcula **uno** de los dos objetivos del mes y devuelve el mes releído, con su cifra y su fecha
 * de cálculo ya actualizadas.
 *
 * Es la única escritura de la Tarjeta A que no es un dato tecleado, y existe porque desde
 * D-OBJ-BOTON (#249) los objetivos **no** se recalculan al abrir la pantalla ni al cambiar de mes:
 * se calculan una vez al crearse la ficha del mes y después solo cuando una persona pulsa su botón.
 *
 * Es un `POST` y **no es idempotente**: pulsarlo dos veces deja dos fechas de cálculo distintas, que
 * es justo lo que la pantalla enseña.
 *
 * Los dos objetivos son independientes: recalcular el de facturación no toca el de cobro ni su
 * fecha, y al revés.
 *
 * @param {Number} anio el año del mes.
 * @param {Number} mes el mes natural, de 1 a 12.
 * @param {String} magnitud cuál de los dos: uno de `FactCobro.OBJETIVOS`. Lo normal es pasar la
 *                 `magnitud` que trae el propio bloque de resumen del mes.
 */
FactCobro.recalcularObjetivo = function(anio, mes, magnitud) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/' + anio + '/' + mes
            + '/objetivo/' + magnitud + '/recalcular',
        method: 'POST',
        data: {}
    }, false);
};

/**
 * **A quién se le puede mandar** el informe del control diario (#547): los grupos de destinatarios
 * —los roles de la ERP— y las personas que hay en cada uno ahora mismo, con su correo.
 *
 * Es una lectura y no escribe nada. No lleva mes: la lista de personas no depende del mes.
 *
 * Lo que se enseña y lo que se manda **no es lo mismo**: el correo se enseña para poder comprobar a
 * dónde va el informe, pero al enviar se manda el `username` y el buzón lo vuelve a resolver el
 * servidor. Así un correo corregido en la ficha después de abrir el diálogo no se queda con el valor
 * antiguo.
 */
FactCobro.destinatariosInforme = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/destinatarios',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Manda por correo el informe del mes del control diario: el botón **Enviar reporte** (#547).
 *
 * El correo lo compone la API con **la misma lectura** que sirve la pantalla y el PDF, y lleva el
 * informe completo **en el cuerpo y sin adjuntos** —dirección quitó el PDF adjunto el 18/10/2026:
 * duplicaba lo que ya va en el cuerpo, y el papel se saca de la pantalla con «Exportar a PDF»—.
 * Mandarlo **no recalcula ningún objetivo** (D-OBJ-BOTON, #249) y no escribe nada del mes.
 *
 * Devuelve a **cuántos buzones** ha salido, para que la pantalla pueda decir cuántos en vez de un
 * «enviado» que nadie puede comprobar. Ese número lo cuenta el servidor: entre lo que se pide y lo
 * que sale hay transformaciones —repetidos que se juntan, y el modo pruebas que sustituye todos los
 * destinatarios por los de test—.
 *
 * @param {Number} anio el año del mes que está en pantalla.
 * @param {Number} mes el mes natural, de 1 a 12.
 * @param {Object} datos `{grupos, personas, emails, emailsCc, emailsCco, asunto}`. Las tres primeras
 *        vías se suman y se resuelven en el servidor; sin ninguna de las tres contesta que no hay a
 *        quién mandarlo, y eso es un error que la pantalla tiene que enseñar.
 */
FactCobro.enviarInformeMes = function(anio, mes, datos) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/' + anio + '/' + mes
            + '/enviar',
        method: 'POST',
        contentType: 'application/json',
        data: datos || {}
    }, false);
};

/**
 * Las **programaciones de envío** del informe del control diario: el «Programar reporte» (#549).
 *
 * POR QUÉ ESTAS CINCO LLAMADAS NO VAN A `/notificacion`, que es donde viven las de OEA y OEB. El
 * **motor es el mismo** —las reglas se guardan en la misma colección, las ejecuta el mismo
 * planificador y se registran en el mismo historial; la #549 prohíbe crear uno nuevo—, pero
 * `/notificacion` exige ADMIN, DIRECTOR o **DTYCAL**, y DTYCAL no ve esta pestaña: es de ADMIN y
 * DIRECTOR (D-ROL, #167). Con la pantalla llamando allí, alguien con DTYCAL podría programarse las
 * cifras de facturación de la empresa a su buzón; y recortar esa lista cambiaría quién programa los
 * informes de las otras pestañas, que el criterio 7 de la incidencia prohíbe tocar. Es lo mismo que
 * ya hizo «Abonos abiertos» (#193) con sus cinco llamadas.
 *
 * La lista viaja en `list`, no en `dto`.
 */
FactCobro.programacionesControlDiario = function() {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/programaciones',
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Da de alta o modifica una programación de envío del control diario.
 *
 * El **módulo lo pone el servidor**, así que no hace falta mandarlo y mandarlo no sirve de nada: es
 * lo que impide que por aquí se escriba una regla de otra pestaña. Y **de qué mes es el correo no se
 * manda**: el envío programado lleva siempre el mes en curso, leído en el momento de salir. Eso es
 * justo lo que lo distingue del botón «Enviar reporte», que manda el mes que está abierto.
 *
 * `visto` es el `modificadaEn` que la pantalla tenía delante, en milisegundos, y solo lo mira una
 * edición: si la regla ya no está así, no se escribe nada y se contesta que recargues («el último
 * estado confirmado no puede reemplazarse con una foto antigua», @Donchurru 15/10/2026).
 *
 * UN ALTA LLEVA ADEMÁS SU CLAVE, y es obligatoria. Una edición no la necesita —ya va condicionada al
 * `visto`, así que su reintento no escribe nada—, pero un **alta no tiene nada delante que repetir**:
 * si el servidor crea la regla y la respuesta se pierde con un 408, un 504 o un `status === 0`,
 * `sendRequest` rechaza la promesa, el `catch` vuelve a habilitar «Guardar» y el reintento crearía
 * una **segunda regla activa idéntica**, con lo que el informe saldría dos veces en cada ejecución.
 * Con la clave, el alta entra en el **registro de operaciones del módulo**: la clave se acepta con
 * un `insert` atómico, la escritura se marca antes de lanzarla y el reintento recibe la regla que se
 * guardó —o un «no se sabe cómo acabó» si la escritura quedó en el aire—, nunca una segunda regla.
 *
 * LO QUE TIENE QUE HACER QUIEN LLAMA: **conservar la clave entre reintentos y guardarla en el
 * navegador antes de lanzar esto**. Una clave nueva por pulsación convierte el reintento en un alta
 * distinta, y una clave que solo vive en memoria no sobrevive a una recarga: en los dos casos el
 * servidor deja de poder reconocer el reintento. Lo hace `facturacion-control-diario.vue` con
 * `operacionesAbonosCobros.js`, y después pregunta por {@code estadoOperacionControlDiario}.
 *
 * @param {Object} programacion `{id?, visto?, nombre, descripcion, frecuencia, hora, diaSemana,
 *        diaMes, soloLaborables, grupos, personas, emails, activa}`. Las tres vías de destinatarios
 *        se guardan tal cual —códigos de grupo y `username`s, nunca buzones— y se resuelven en cada
 *        envío, así que un alta o una baja de personal cuenta sin tocar la regla.
 * @param {String} operacion la clave del alta, la misma en los reintentos. En una edición no se usa.
 */
FactCobro.guardarProgramacionControlDiario = function(programacion, operacion) {
    const self = this;
    return self.sendRequest({
        url: conOperacion(self.server_movilidad + self.prefix + self.model + '/mes/programaciones',
            operacion),
        method: 'POST',
        contentType: 'application/json',
        data: programacion || {}
    }, false);
};

/**
 * **Cómo acabó una operación del alta de una programación del control diario** (#549).
 *
 * POR QUÉ HACE FALTA. La clave de un alta se guarda en el navegador **antes** de lanzar el `POST`
 * (`operacionesAbonosCobros.js`), así que sobrevive a una recarga, a una pestaña cerrada y a un «ir
 * atrás». Al volver, lo que no se puede hacer es deducir qué pasó mirando la lista de
 * programaciones: ahí no se distingue «no se creó» de «se creó y alguien la ha borrado», y las dos
 * cosas piden lo contrario. Se pregunta.
 *
 * QUÉ CONTESTA. Lo mismo que {@code estadoOperacionAbonos} —es el mismo registro—: `NO_CONSTA` y
 * `RETOMABLE` quieren decir que **no se ha aplicado nada** y se puede repetir; `EN_CURSO`, que hay
 * que esperar; `DESCONOCIDO`, que **puede estar creada** y hay que mirar la lista; `HECHA`, que
 * terminó, y `reglaId` dice con qué regla.
 *
 * ES DE SOLO LECTURA DE VERDAD: no reserva, ni marca, ni relanza nada, así que preguntar en cada
 * apertura del diálogo no tiene consecuencias. Y solo contesta por las operaciones de quien pregunta
 * y de este informe: la clave la compone el servidor con el usuario autenticado y con el módulo.
 */
FactCobro.estadoOperacionControlDiario = function(operacion) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/programaciones/operaciones/'
            + encodeURIComponent(operacion),
        method: 'GET',
        data: {}
    }, false);
};

/**
 * Activa o pausa una programación.
 *
 * `activaActual` es **lo que la pantalla tenía delante**, y el servidor escribe el contrario solo si
 * la regla sigue así. No es un dato redundante: con un «lee, niega y guarda» en el servidor, dos
 * pulsaciones casi a la vez leen las dos lo mismo y la segunda deshace la primera sin que nadie lo
 * diga.
 */
FactCobro.alternarProgramacionControlDiario = function(id, activaActual) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/programaciones/' + id
            + '/activa?activaActual=' + (activaActual ? 'true' : 'false'),
        method: 'PUT',
        contentType: 'application/json',
        data: {}
    }, false);
};

/**
 * Borra una programación, y con eso **no queda ningún envío suelto** (criterio 2 de la #549): el
 * planificador no lleva cola propia, lee las reglas activas en cada pasada, así que al día siguiente
 * no llega nada. Lo que queda es el historial de lo que ya se mandó, que no es un envío pendiente.
 */
FactCobro.borrarProgramacionControlDiario = function(id, visto) {
    const self = this;
    const desde = visto === null || visto === undefined ? '' : '?visto=' + visto;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/programaciones/' + id + desde,
        method: 'DELETE',
        data: {}
    }, false);
};

/**
 * El **historial de envíos** de este informe: qué salió, cuándo, a cuántos buzones y con qué
 * resultado (`ENVIADO`, `PARCIAL` o `ERROR`, con el motivo cuando hay).
 *
 * Es el registro de siempre (`notificacion_logs`) filtrado por el módulo de este informe, así que
 * por aquí no se ven los envíos de OEA, de OEB ni los de abonos abiertos. Cuando un envío falla
 * —incluido el mes sin ficha, que no manda correo— este registro es lo único que lo cuenta
 * (criterio 8).
 *
 * La lista viaja en `list`, no en `dto`.
 */
FactCobro.enviosProgramadosControlDiario = function(limit) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/mes/programaciones/envios',
        method: 'GET',
        data: { limit: limit || 20 }
    }, false);
};

/**
 * Las tres frecuencias que el planificador ejecuta de verdad, y los días de la semana que entiende.
 * Están aquí, en el adaptador, porque son **parte del contrato**: los valida
 * `FactCobroPeriodicidad` en la API y con la lista escrita en el `.vue` una frecuencia mal puesta se
 * vería como un 400 al guardar y no antes.
 *
 * `PASO_MINUTOS` es cada cuántos minutos despierta el planificador: una hora que no sea múltiplo de
 * eso **se ejecuta antes de su hora** —una regla a las 08:03 sale a las 08:00—, así que la API la
 * rechaza en vez de redondearla a escondidas (D-COB-8).
 */
FactCobro.FRECUENCIAS = ['DIARIA', 'SEMANAL', 'MENSUAL'];

FactCobro.DIAS_SEMANA = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO', 'DOMINGO'];

FactCobro.PASO_MINUTOS = 5;

/** El día del mes más alto que se acepta: el 28 existe en todos los meses y el 29 no. */
FactCobro.DIA_MES_MAXIMO = 28;

/**
 * Descarga el **PDF del mes** del control diario (#173): lo mismo que se ve en pantalla, en un A4
 * que se puede llevar a una reunión y archivar.
 *
 * El documento lo genera la API (`GET /factcobro/mes/{anio}/{mes}/pdf`) y aquí solo se descarga: es
 * el mismo mes que pinta la pantalla, así que las cifras del papel son las de la pantalla sin que el
 * navegador tenga que volver a formatear ni a sumar nada.
 *
 * Va por `request.promise` y no por `sendRequest` porque la respuesta **no es JSON**: `sendRequest`
 * desenvuelve `{dto}` / `{error}` y aquí lo que llega son los bytes del PDF. Ir por su cuenta
 * significa que lo que `sendRequest` hace para TODAS las peticiones autenticadas hay que repetirlo
 * aquí, y son cuatro cosas, no una: el `Authorization` leído de `localStorage`, la forma del rechazo
 * (`{status, code, message}`) que ya entienden las pantallas de la pestaña, el **cierre de sesión en
 * un 401** y el aviso de **timeout de pasarela**. Mismo criterio y misma forma que en `RhFestivo`,
 * que es la otra petición del repo que sale de `sendRequest`.
 *
 * El `Authorization` se repite aquí por lo mismo que en `RhFestivo`: la cabecera global de
 * `Framework7.request.setup` se queda con el token viejo cuando la sesión se renueva sin recargar la
 * aplicación, y entonces el servidor contesta 401 teniendo sesión válida.
 *
 * Y **el PDF no llega a una sesión que ya no es la que lo pidió** (rondas 1, 2 y 3 del 07 y el
 * 09/10/2026): ver `sigueSiendoLaSesionDe`, que es el único sitio que lo decide, y `descargar`, que
 * se lo pregunta **antes de cada paso que no se puede deshacer** y no una sola vez al principio.
 */
FactCobro.pdfMes = function(anio, mes) {
    const self = this;
    // Token con el que sale la petición: si la sesión se renueva mientras el servidor compone el
    // PDF, el 401 que llegue es del token viejo y no hay que echar a quien acaba de entrar.
    const tokenEnviado = tokenDeSesion();
    // La sesión que pide, que NO es el token: ver `FactCobro.sesionActual`.
    const sesionQuePide = FactCobro.sesionActual();
    const siSigueSiendoSuya = sigueSiendoLaSesionDe(sesionQuePide);
    return self.request.promise({
        url: self.server_movilidad + self.prefix + self.model + '/mes/' + anio + '/' + mes + '/pdf',
        method: 'GET',
        crossDomain: true,
        headers: cabeceraDeSesion(),
        xhrFields: { responseType: 'arraybuffer' }
    }).then(
        siSigueSiendoSuya(function(res) {
            // El guardado va en el manejador de ÉXITO y no detrás del de fallo a propósito: así su
            // fallo —el fichero que el móvil no consigue abrir— no pasa por `rechazoBinario`, que es
            // la traducción de un fallo HTTP y dejaría ese caso sin mensaje.
            //
            // Y el guardián viaja con él: que la sesión sea la misma AHORA no dice nada de lo que
            // será cuando el fichero esté escrito y el visor se abra.
            return descargar(res.data, nombreDelPdf(anio, mes), esDeEstaSesion(sesionQuePide));
        }),
        siSigueSiendoSuya(function(e) {
            return Promise.reject(rechazoBinario(e, tokenEnviado));
        })
    );
};

/**
 * **Cuántas veces se ha cerrado una sesión** desde que se cargó la aplicación: la generación que
 * distingue dos sesiones de la misma cuenta.
 *
 * Cuelga de `app:session:close` y no de `app:session:open` **a propósito**, y es justo la
 * distinción que pedía el hallazgo: `saveSession` —y con ella `app:session:open`— la llama también
 * la aplicación móvil cuando **renueva** el token por su cuenta (`app.vue`, el reintento que se
 * autentica con las credenciales guardadas), y eso no es otra sesión. `removeSession` es el único
 * camino que cierra una: borra `session` y `user`, olvida el usuario y lleva al login.
 *
 * Vive en el módulo y no en `localStorage` porque lo que tiene que distinguir son dos sesiones de
 * **esta** carga de la aplicación: lo que sobrevive a una recarga del navegador no es ninguna
 * petición en vuelo, así que una generación persistida solo serviría para envejecer sola.
 */
let cierresDeSesion = 0;

EventBus.$on('app:session:close', function() {
    cierresDeSesion += 1;
});

/**
 * **La sesión que está delante**, con la forma de un sello opaco: dos sellos distintos son dos
 * sesiones distintas y no hay nada más que leer dentro.
 *
 * Es `localStorage` y no el token **a propósito**, y es la diferencia entre las dos cosas que se
 * confunden todo el rato en esta petición:
 *
 *  - **La sesión se renueva** (el token cambia y la cuenta no): en Cordova la aplicación se vuelve a
 *    identificar por su cuenta con las credenciales guardadas (`app.vue`, `saveSession` del
 *    reintento), sin que nadie toque nada. Eso **no** es otra sesión: el PDF que viene en camino
 *    sigue siendo de quien lo pidió y tiene que descargarse. Por eso el sello no lleva el token.
 *  - **La sesión cambia**: se cierra (`removeSession` borra `session` y `user`) o entra otra cuenta.
 *    Ahí la respuesta ya no es de quien está delante.
 *
 * La cuenta sin sesión abierta no es la misma cosa que la misma cuenta con sesión, de ahí las dos
 * mitades: cerrar sesión y no entrar con nadie también es un cambio de sesión.
 *
 * **Y la cuenta no basta para decir cuál es la sesión**, que es lo que faltaba (ronda 2 del
 * 07/10/2026): cerrar sesión y volver a entrar con el mismo usuario devolvía exactamente el sello
 * anterior, así que el PDF que había pedido la sesión cerrada se daba por bueno y los avisos de la
 * pantalla anterior reaparecían en la nueva. De ahí la tercera mitad, `cierresDeSesion`: lo que
 * hace distintas a dos sesiones de la misma cuenta es el cierre que hubo en medio.
 *
 * Público porque la pantalla lo necesita para lo mismo —nombrar a qué sesión pertenece lo que
 * apunta—, y leerlo dos veces de `localStorage`, cada una a su manera, es tener dos vigilantes que
 * no se hablan: ya costó cuatro rondas en el PR #1759.
 */
FactCobro.sesionActual = function() {
    const token = tokenDeSesion();
    if (!token) return 'cerrada|' + cierresDeSesion;
    let cuenta = '';
    try {
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        cuenta = (user && user.username) || '';
    } catch (e) {
        cuenta = '';
    }
    return 'abierta|' + cuenta + '|' + cierresDeSesion;
};


/**
 * **EL ÚNICO SITIO QUE DECIDE SI ALGO SIGUE SIENDO DE QUIEN LO PIDIÓ** (#173).
 *
 * Es una pregunta, no una comprobación que se haga una vez: `esDeEstaSesion(sesion)` devuelve la
 * función que la contesta, y se contesta **cada vez que hace falta**. Por aquí pasan las dos cosas
 * que no se pueden deshacer:
 *
 *  - `sigueSiendoLaSesionDe`, que envuelve los dos manejadores de la respuesta HTTP;
 *  - `descargar`, y a través de ella `FileUtils.openFile`, que la preguntan antes de escribir el
 *    fichero y antes de abrir el visor.
 *
 * ## Por qué la pregunta y no un `if` al principio
 *
 * Las tres rondas de esta familia dicen lo mismo contado por tres sitios: **el PDF financiero de un
 * mes acabó delante de quien no lo había pedido**. La primera fue que no se comprobaba nada; la
 * segunda, que el sello no distinguía dos sesiones de la misma cuenta; la tercera, que la
 * comprobación se hacía **demasiado pronto**: respondía el servidor, se daba la respuesta por buena,
 * y después venían cuatro pasos asíncronos —reconocer el tipo, crear la entrada, escribir, abrir el
 * visor— en los que la sesión se podía ir.
 *
 * Esa es la causa común: *una foto de la sesión no dice nada de lo que será la sesión cuando llegue
 * el paso irreversible*. Y por eso el planteamiento no es otro `if` en el sitio señalado, sino que
 * **la garantía viaje con la operación** y se pregunte pegada a cada acto: entre la pregunta y el
 * acto no queda ni un paso asíncrono, así que no queda hueco. Los pasos son cuatro y están
 * enumerados: la descarga del navegador, la creación de la entrada, el fin de la escritura y la
 * apertura del visor.
 *
 * Lo que la pregunta **no** mira es el token: la sesión se renueva sola en la aplicación móvil y eso
 * no es otra sesión (ver `FactCobro.sesionActual`).
 */
function esDeEstaSesion(sesionQuePide) {
    return function() {
        return FactCobro.sesionActual() === sesionQuePide;
    };
}

/** El rechazo de lo que ya no es de quien lo pidió: sin mensaje, porque no hay nada que enseñar. */
function rechazoDeOtraSesion() {
    return { status: 0, code: null, message: null, otraSesion: true };
}

/**
 * La pregunta aplicada a **la respuesta HTTP**, para los dos manejadores.
 *
 * Envuelve un manejador —el del éxito o el del fallo, los dos— y lo deja pasar solo si la sesión no
 * ha cambiado desde que salió la petición. Es una sola decisión y no un `if` repetido en cada rama
 * porque las dos ramas tienen que tomarla, y la que se olvide es la que enseña el PDF de otro:
 *
 *  - **El éxito**, que es el caso grave: `descargar` no escribe en ninguna pantalla, abre el visor
 *    del dispositivo o lanza la descarga del navegador, así que llega a quien esté delante **sea
 *    quien sea**. Descartado aquí, el PDF de la cuenta anterior ni se empieza a escribir; y si la
 *    sesión se va más tarde, lo paran los guardianes de `descargar`.
 *  - **El fallo**, por dos motivos: su aviso es para quien exportó y no para quien entró después, y
 *    `rechazoBinario` puede **cerrar la sesión** en un 401 —echando a quien acaba de entrar—. Eso
 *    último lo para además la comparación de token de `sesionRechazada`, que sigue haciendo falta
 *    para el caso contrario: el 401 del token viejo cuando la sesión se renovó sin cambiar.
 *
 * El rechazo sale marcado con `otraSesion` y sin mensaje: no hay nada que enseñar a nadie. Quien
 * exporta lo trata como el `sesionCaducada` que ya traía: suelta el bloqueo del mes —la petición ha
 * terminado— y no escribe ningún aviso.
 */
function sigueSiendoLaSesionDe(sesionQuePide) {
    const vigente = esDeEstaSesion(sesionQuePide);
    return function(manejador) {
        return function(valor) {
            if (!vigente()) {
                return Promise.reject(rechazoDeOtraSesion());
            }
            return manejador(valor);
        };
    };
}

/**
 * Cómo se llama el fichero que se guarda: `control-diario-2026-09.pdf`.
 *
 * El servidor manda el mismo nombre en su `Content-Disposition`, pero el navegador **no lo puede
 * leer** desde aquí: la petición es de otro origen y esa cabecera no está entre las expuestas, así
 * que `getResponseHeader` la devuelve vacía. El nombre se arma con el año y el mes que se acaban de
 * pedir, que es el mismo dato del que sale el del servidor.
 */
function nombreDelPdf(anio, mes) {
    return 'control-diario-' + anio + '-' + (mes < 10 ? '0' + mes : String(mes)) + '.pdf';
}

/**
 * Guarda los bytes como fichero, igual que el resto de las descargas del repo: las tres ramas y en
 * este orden son las de `Documento.download` y `Documento.downloadZip`.
 *
 * La primera rama, la de Cordova, **no es decorativa**: en la aplicación de Android y de iOS un
 * enlace `blob:` con `download` no guarda nada —iOS 11 y 12 lo ignoran—, así que sin ella el botón
 * terminaría su petición sin que apareciera ningún PDF. Ahí el fichero se escribe y se abre con
 * `FileUtils.openFile`, que es lo que hace el visor de documentos.
 *
 * Devuelve promesa porque en Cordova abrir el fichero es asíncrono y puede fallar por su cuenta
 * (sin sitio en el dispositivo, sin el plugin): quien exporta tiene que poder enterarse en vez de
 * ver «Generando…» y nada más.
 *
 * `vigente` es la pregunta de `esDeEstaSesion`, y se hace **pegada a cada acto que no se puede
 * deshacer**, no una vez al entrar:
 *
 *  - en Cordova son cuatro pasos asíncronos hasta que el visor se abre, así que la pregunta baja
 *    hasta `FileUtils.openFile`, que es donde están;
 *  - en el navegador el acto es el `click()` del enlace, y aquí no hay nada asíncrono entre la
 *    pregunta y él.
 *
 * Lo que `openFile` descarta vuelve marcado con `descartado`, y aquí se traduce al mismo rechazo
 * `otraSesion` que ya entiende la pantalla: no es un fallo que haya que contarle a quien está
 * delante, porque no es suyo.
 */
function descargar(datos, nombre, vigente) {
    const sigueSiendo = typeof vigente === 'function' ? vigente : function() { return true; };
    if (typeof cordova != 'undefined') {
        return new Promise(function(resolve, reject) {
            FileUtils.openFile(datos, nombre, resolve, function(fallo) {
                if (fallo && fallo.descartado) {
                    return reject(rechazoDeOtraSesion());
                }
                return reject({
                    status: 0,
                    code: null,
                    message: 'El PDF se ha generado pero no se ha podido abrir en el dispositivo: '
                        + ((fallo && fallo.error) || 'no hay con qué abrirlo.')
                });
            }, sigueSiendo);
        });
    }
    if (!sigueSiendo()) return Promise.reject(rechazoDeOtraSesion());
    const blob = new Blob([datos], { type: 'application/pdf' });
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, nombre);
        return Promise.resolve();
    }
    const enlace = document.createElement('a');
    enlace.href = window.URL.createObjectURL(blob);
    enlace.download = nombre;
    enlace.target = '_blank';
    enlace.click();
    URL.revokeObjectURL(enlace.href);
    return Promise.resolve();
}

/**
 * El rechazo de una petición que pedía bytes, con la forma que esperan las pantallas.
 *
 * Hace falta porque el cuerpo de error **sí** es JSON pero llega como `ArrayBuffer` —lo pidió el
 * `responseType`—, así que el `JSON.parse(xhr.response)` de `DatabaseModel.sendRequest` revienta y
 * el motivo que escribió el servidor se pierde justo en los casos en los que hay algo concreto que
 * decir: la pestaña cerrada (#169) y el 403 de rol.
 *
 * Y aquí se cierra también la sesión en un 401, que es lo que hace `sendRequest` para cualquier otra
 * petición: sin esto, un token caducado o revocado dejaba a quien exporta con un error de
 * exportación y la sesión inválida puesta, pudiendo seguir pulsando botones que ya no pueden ir.
 */
function rechazoBinario(e, tokenEnviado) {
    const rechazo = { status: (e && e.status) || 0, message: (e && e.message) || null, code: null };
    const xhr = e && e.xhr;
    if (!rechazo.status && xhr && xhr.status) rechazo.status = xhr.status;
    // La misma lista y el mismo criterio que `sendRequest` y `RhFestivo`: la petición se cerró por
    // arriba, no por el servidor. Aquí el aviso NO dice «recarga antes de reintentar» como el de
    // `sendRequest`, porque esto es una lectura y no ha podido aplicar nada: se reintenta y ya.
    if (esTimeoutDePasarela(rechazo.status)) {
        rechazo.message = 'El PDF ha tardado demasiado y la conexión se ha cerrado. Vuelve a '
            + 'intentarlo: exportar no cambia nada, así que no se ha quedado nada a medias.';
        return rechazo;
    }
    const cuerpo = xhr && xhr.response;
    let codigoOauth = null;
    if (cuerpo) {
        try {
            const error = JSON.parse(comoTexto(cuerpo)).error;
            // `error` es un objeto en los errores de la API y una cadena en los de OAuth
            // (`"error": "invalid_token"`), que es justamente el caso del token rechazado.
            if (typeof error === 'string') {
                codigoOauth = error;
            } else if (error) {
                rechazo.message = error.message || rechazo.message;
                rechazo.code = error.code || null;
            }
        } catch (ignorado) {
            // Un cuerpo que no es el JSON de la API —una pasarela que corta, un 500 sin cuerpo— deja
            // el rechazo con su status y sin mensaje, y la pantalla enseña su texto genérico.
        }
    }
    if (rechazo.status === 401 || codigoOauth === 'invalid_token') {
        return sesionRechazada(rechazo, tokenEnviado);
    }
    return rechazo;
}

/**
 * El tratamiento de sesión del 401, igual que el de `DatabaseModel.sendRequest`: cerrar la sesión y
 * mandar al login, que es lo que `removeSession` hace por su cuenta.
 *
 * Con la salvedad de `RhFestivo`, y por el mismo motivo: si el token guardado **ya no es** el que
 * salió con la petición, la sesión se ha renovado mientras el servidor componía el PDF y el 401 es
 * del token viejo. Cerrar la sesión ahí echaría a quien acaba de entrar.
 *
 * El rechazo lleva `sesionCaducada` para que la pantalla no suelte un aviso encima del login al que
 * se está navegando, como ya hace la de los boletines de festivos.
 */
function sesionRechazada(rechazo, tokenEnviado) {
    const app = window.app;
    if (tokenEnviado && tokenDeSesion() !== tokenEnviado) {
        rechazo.message = 'La sesión se ha renovado mientras se generaba el PDF y la descarga se ha '
            + 'quedado sin permiso. Vuelve a exportar: no se ha cambiado nada.';
        return rechazo;
    }
    if (app && app.methods && typeof app.methods.removeSession === 'function') {
        app.methods.removeSession();
        if (app.preloader) app.preloader.hide();
        rechazo.sesionCaducada = true;
        rechazo.message = 'La sesión ha caducado.';
    }
    return rechazo;
}

/**
 * Misma lista que `DatabaseModel.sendRequest` y que `RhFestivo`: 520/522/524 son de Cloudflare y el
 * 0 es la conexión cortada sin respuesta.
 */
function esTimeoutDePasarela(status) {
    return [408, 504, 520, 522, 524, 598, 599].indexOf(status) !== -1 || status === 0;
}

/**
 * El cuerpo de la respuesta como texto. Con `TextDecoder` cuando lo hay, porque los mensajes de esta
 * pestaña llevan tildes y eñes y byte a byte saldrían con la acentuación roto; si no lo hay, byte a
 * byte, que para saber el `code` del error basta.
 */
function comoTexto(cuerpo) {
    if (typeof cuerpo === 'string') return cuerpo;
    const bytes = new Uint8Array(cuerpo);
    if (typeof TextDecoder !== 'undefined') return new TextDecoder('utf-8').decode(bytes);
    return bytes.reduce(function(acc, byte) { return acc + String.fromCharCode(byte); }, '');
}

/** El token de la sesión guardada, o `null`. */
function tokenDeSesion() {
    try {
        const session = JSON.parse(localStorage.getItem('session') || 'null');
        return (session && (session.token || session.access_token)) || null;
    } catch (e) {
        return null;
    }
}

/** El mismo `Authorization` que pone `sendRequest`, leído de `localStorage`. */
function cabeceraDeSesion() {
    const token = tokenDeSesion();
    // `headers` sustituye entero al global de Framework7, así que sin token se manda un objeto
    // vacío y la petición sale sin Authorization: es mejor el 401 limpio que una cabecera a
    // medias.
    return token ? { Authorization: 'Bearer ' + token } : {};
}

export default FactCobro;
